import type { AllBaseLineItemResponse, RepairTreeItem } from '@/views/dashboard/safe-status/base-line/repair-item-tree-type'
import { baselineRepairNameI18nKey } from '@/views/dashboard/safe-status/base-line/repair-item-tree-type'
import { i18n } from '@/i18n'

export const DUBA_BASELINE_REPAIR_ITEMS_KEY = 'DUBA_BASELINE_REPAIR_ITEMS'

let cachedRaw: string | null = undefined
let cachedParsed: AllBaseLineItemResponse | null | undefined = undefined

function parseRepairItemsFromStorage (): AllBaseLineItemResponse | null {
  if (typeof localStorage === 'undefined') return null
  const raw = localStorage.getItem(DUBA_BASELINE_REPAIR_ITEMS_KEY)
  if (raw === cachedRaw && cachedParsed !== undefined) {
    return cachedParsed
  }
  cachedRaw = raw
  if (!raw) {
    cachedParsed = null
    return null
  }
  try {
    cachedParsed = JSON.parse(raw) as AllBaseLineItemResponse
    return cachedParsed
  } catch {
    cachedParsed = null
    return null
  }
}

/** 缓存中无检查项 JSON 时拉取并写入 localStorage（由调用方注入与 BaseLineService.getAllBaselineItems 等价的请求） */
export async function ensureBaselineRepairItemsLoaded (fetchCheckItems: () => Promise<void>): Promise<void> {
  if (typeof localStorage === 'undefined') return
  if (localStorage.getItem(DUBA_BASELINE_REPAIR_ITEMS_KEY)) return
  await fetchCheckItems()
  invalidateBaselineRepairItemsCache()
}

export function hasBaselineRepairItemsInStorage (): boolean {
  if (typeof localStorage === 'undefined') return false
  return !!localStorage.getItem(DUBA_BASELINE_REPAIR_ITEMS_KEY)
}

/** 可自定义阈值的检测项默认值（i18n {0} 占位的 fallback） */
const CUSTOMIZABLE_ITEM_DEFAULTS: Record<string, number> = {
  '1.1.3': 8,
  '1.1.4': 2,
  '1.1.5': 90,
  '1.1.6': 5,
  '1.2.1': 30,
  '1.2.2': 8,
  '1.2.3': 30,
}

/** API 返回的自定义阈值缓存（teamconfig/get base_lime_custom），优先于 CUSTOMIZABLE_ITEM_DEFAULTS */
const apiCustomThresholds: Record<string, number> = {}

/** 将 API 返回的自定义阈值写入全局缓存，所有 translateBaselineRepairItemName() 调用自动生效 */
export function setCustomThresholds (values: Record<string, number>): void {
  Object.assign(apiCustomThresholds, values)
}

/** 按 id 取展示名：优先 API 自定义值 > 硬编码默认值 > i18n 缺键时用接口/缓存原文 */
export function translateBaselineRepairItemName (id: string, fallbackFromCache: string, value?: number): string {
  if (!id) return fallbackFromCache || ''
  const key = baselineRepairNameI18nKey(id)
  const t = i18n.global.t.bind(i18n.global)
  const defaultVal = apiCustomThresholds[id] ?? CUSTOMIZABLE_ITEM_DEFAULTS[id]
  const params = defaultVal !== undefined ? [value ?? defaultVal] : undefined
  const v = t(key, params)
  if (v === key) return fallbackFromCache || ''
  return v
}

/** 深拷贝树并为各节点 `name` 套上 i18n（handle_type / scan_level 等仍来自缓存） */
export function applyBaselineRepairDisplayNamesToTree (roots: RepairTreeItem[] | undefined): RepairTreeItem[] {
  if (!roots?.length) return []
  const clone = JSON.parse(JSON.stringify(roots)) as RepairTreeItem[]
  const walk = (nodes: RepairTreeItem[]): void => {
    for (const n of nodes) {
      n.name = translateBaselineRepairItemName(String(n.id), n.name || '')
      if (n.items?.length) walk(n.items)
    }
  }
  walk(clone)
  return clone
}

/** 先深入子节点，仅在无 children 的节点上按 id 匹配，避免与分类节点 id 冲突 */
function findCheckItemInForest (
  roots: RepairTreeItem[] | undefined,
  checkItemId: string,
  parentCategoryName: string,
  /** 当前 `roots` 所属父节点的 id（用于叶子检测项的二级分类 i18n） */
  parentNodeId: string
): { itemName: string; categoryName: string; itemId: string; categoryId: string } | null {
  if (!roots?.length || !checkItemId) return null
  for (const node of roots) {
    if (node.items?.length) {
      const cat = (node.name || parentCategoryName || '').trim()
      const found = findCheckItemInForest(node.items, checkItemId, cat, String(node.id))
      if (found) return found
    }
    if (!node.items?.length && String(node.id) === String(checkItemId)) {
      return {
        itemName: node.name,
        categoryName: node.fatherName || parentCategoryName || '',
        itemId: String(node.id),
        categoryId: parentNodeId,
      }
    }
  }
  return null
}

/**
 * 用 localStorage 中 DUBA_BASELINE_REPAIR_ITEMS（与修复项树同源）按 check_item_id 解析检测项名称与分类名。
 * 展示名按 id 走 i18n，缺键时回退缓存中的原文。
 */
export function getBaselineCheckItemLabelsById (checkItemId: string): {
  itemName: string
  categoryName: string
} {
  const data = parseRepairItemsFromStorage()
  if (!data) {
    return { itemName: '', categoryName: '' }
  }
  const fromWin = findCheckItemInForest(data.category, checkItemId, '', '')
  const hit = fromWin || findCheckItemInForest(data.linux_category, checkItemId, '', '')
  if (!hit) {
    return { itemName: '', categoryName: '' }
  }
  return {
    itemName: translateBaselineRepairItemName(hit.itemId, hit.itemName),
    categoryName: translateBaselineRepairItemName(hit.categoryId, hit.categoryName),
  }
}

/** 列表刷新后若缓存已更新，可调用以重新读 localStorage */
export function invalidateBaselineRepairItemsCache () {
  cachedRaw = undefined
  cachedParsed = undefined
}

/** 终端列表行：优先 non_compliance_count，否则 substandard_num */
export function resolveTerminalNonComplianceCount (row: {
  non_compliance_count?: number
  substandard_num?: number
}): number {
  const n = row.non_compliance_count
  if (n !== undefined && n !== null) return Number(n) || 0
  return Number(row.substandard_num) || 0
}

/** 检测项统计行：优先 non_compliance_count，否则 substandard_terminal_count */
export function resolveCheckItemNonComplianceCount (row: {
  non_compliance_count?: number
  substandard_terminal_count?: number
}): number {
  const n = row.non_compliance_count
  if (n !== undefined && n !== null) return Number(n) || 0
  return Number(row.substandard_terminal_count) || 0
}
